// WHAT'S BLOCKING QUOTES -- Admin only, read only (data-readiness, 2026-09-30).
//
// Every DRAFT that cannot price or cannot finalise, grouped by the reason, each
// reason naming its quotes with a link. It answers "what do I need to enter?"
// (a duty rate for this HSN, an FX rate for that currency, a customer's
// State...) without opening drafts one by one.
//
// ⚠️ THE PAGE ONLY FORMATS. The backend builds every verdict from the same
// resolvers the save path uses (models/readiness.py), so this page cannot
// disagree with the save about what is missing; nothing is computed here and
// nothing is written. ⚠️ ADMIN ONLY: the sidebar item and this page are hidden
// for anyone else, and /anixify/api/readiness refuses them with a 403.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getReadiness, type ReadinessReport } from "@/services/api";
import { useIsAdminOrFalse } from "@/auth/AuthContext";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

export default function ReadinessPage() {
  const isAdmin = useIsAdminOrFalse();
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getReadiness();
    setLoading(false);
    if (!r.ok || !r.report) {
      setReport(null);
      setError(r.error ?? "The report could not be loaded.");
      return;
    }
    setError("");
    setReport(r.report);
  }, []);
  useEffect(() => { if (isAdmin) void load(); else setLoading(false); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div data-testid="readiness-forbidden" style={{ padding: spacing[24], color: semanticColors.textSecondary }}>
        This page is for Admins.
      </div>
    );
  }

  return (
    <div data-testid="readiness-page" style={{ padding: spacing[24] }}>
      <div style={{ marginBottom: spacing[16] }}>
        <div style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
                      color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight }}>
          What&rsquo;s blocking quotes
        </div>
        <div data-testid="readiness-subtitle"
             style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
          Drafts that cannot price or cannot finalise, by reason. Read only.
          {report && (
            <> {report.blocked_quotes} draft{report.blocked_quotes === 1 ? "" : "s"} blocked.</>
          )}
          {" "}
          <button data-testid="readiness-refresh" onClick={() => void load()} disabled={loading}
                  style={{ height: heights.control, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
                           borderRadius: radius.base, backgroundColor: colors.white, cursor: "pointer" }}>
            Refresh
          </button>
        </div>
      </div>

      {loading && <div style={{ color: semanticColors.textSecondary }}>Loading…</div>}
      {error && (
        <div data-testid="readiness-error" role="alert" style={{ color: semanticColors.destructive }}>{error}</div>
      )}
      {report && report.truncated && (
        <div data-testid="readiness-truncated" style={{ color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
          Only the first 500 drafts were checked.
        </div>
      )}
      {report && report.groups.length === 0 && !loading && (
        <div data-testid="readiness-clear"
             style={{ padding: spacing[16], border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                      backgroundColor: semanticColors.panelBg }}>
          Nothing is blocking any draft.
        </div>
      )}

      {report?.groups.map((g) => (
        <section key={g.key} data-testid={`readiness-group-${g.key}`}
                 style={{ marginBottom: spacing[16], border: `1px solid ${semanticColors.border}`,
                          borderRadius: radius.base, backgroundColor: semanticColors.panelBg }}>
          <div style={{ padding: `${spacing[8]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}` }}>
            <div style={{ fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
              {g.title}
            </div>
            <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>{g.hint}</div>
          </div>
          {g.items.map((item) => (
            <div key={item.label} data-testid={`readiness-item-${g.key}`}
                 style={{ padding: `${spacing[6]}px ${spacing[12]}px`, display: "flex", gap: spacing[12],
                          flexWrap: "wrap", alignItems: "baseline" }}>
              <span style={{ fontWeight: typography.fontWeight.medium, minWidth: 160 }}>{item.label}</span>
              <span style={{ display: "flex", gap: spacing[8], flexWrap: "wrap" }}>
                {item.quotes.map((q) => (
                  <Link key={q.quote_id} to={`/saved-quotes/${q.quote_id}`}
                        data-testid={`readiness-quote-${g.key}-${q.quote_id}`}
                        style={{ color: semanticColors.accentInk, textDecoration: "underline" }}>
                    {q.name}
                  </Link>
                ))}
              </span>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
